/* global HOME_LOADER_MINIMUM_DURATION, HOME_LOADER_MAXIMUM_DURATION */

import { Controller } from 'stimulus';
import gsap from 'gsap';
import prefersReducedMotion from '../helpers/prefersReducedMotion';
import sourcesFromElement from '../helpers/homeVideo/sourcesFromElement';
import resolveSourceCandidate from '../helpers/homeVideo/resolveSourceCandidate';
import createTrackElement from '../helpers/homeVideo/createTrackElement';
import createVideoElementFromSource from '../helpers/homeVideo/createVideoElementFromSource';
import triggerWordSplit from '../helpers/triggerWordSplit';
import resolvePosterCandidate from '../helpers/homeVideo/resolvePosterCandidate';
import posterSourcesFromElement from '../helpers/homeVideo/posterSourcesFromElement';
import universalTracking from '../tracking/universalTracking';

class HomeVideoController extends Controller {
  connect() {
    // Time when we started the video loading process
    this.startLoadingTime = Math.round(Date.now() / 1000);

    this.shownEnding = false;

    this.shownCookieConsent = false;

    this.loaderTimeout = null;

    const sourceCandidate = resolveSourceCandidate(
      sourcesFromElement(this.videoWrapperTarget),
    );

    if (!sourceCandidate) {
      throw new Error('Could not find source candidate for video');
    }

    const posterCandidate = resolvePosterCandidate(
      posterSourcesFromElement(this.videoWrapperTarget),
    );

    const videoElement = createVideoElementFromSource(sourceCandidate, posterCandidate);

    if (this.videoWrapperTarget.getAttribute('data-subtitle-track')) {
      const trackElement = createTrackElement(videoElement, this.videoWrapperTarget.getAttribute('data-subtitle-track'));

      videoElement.appendChild(trackElement);
    }

    const videoWrapperElement = document.createElement('div');
    videoWrapperElement.className = 'home-video__wrapper';
    videoWrapperElement.appendChild(videoElement);

    this.videoWrapperTarget.appendChild(videoWrapperElement);

    // In case we haven't heard from the video after a specified time, we've a fallback in place
    this.loaderTimeout = setTimeout(() => {
      this.togglePlayButton(true);
      this.toggleLoader(false);
    }, HOME_LOADER_MAXIMUM_DURATION * 1000);

    this.handleVideoAutoplay();
    this.handleVideoEnding();
  }

  togglePlayButton(state) {
    this.playButtonTarget.setAttribute('aria-hidden', state ? 'false' : 'true');
  }

  onPlay() {
    const video = this.videoWrapperTarget.querySelector('video');

    if (video) {
      video.play();
      this.togglePlayButton(false);
    }
  }

  toggleLoader(state) {
    requestAnimationFrame(() => {
      gsap.to(this.loaderTarget, {
        autoAlpha: 0,
        duration: 2,
      });

      this.loaderTarget.setAttribute('aria-busy', state ? 'true' : 'false');
    });
  }

  handleVideoAutoplay() {
    const video = this.videoWrapperTarget.querySelector('video');

    video.addEventListener('canplaythrough', () => {
      const canPlayThroughReadyTime = Math.round(Date.now() / 1000);
      const additionalWaitTime = (
        HOME_LOADER_MINIMUM_DURATION - (canPlayThroughReadyTime - this.startLoadingTime)
      );

      setTimeout(() => {
        // Don't autoplay the video when the user prefers no motion.
        if (prefersReducedMotion()) {
          this.togglePlayButton(true);
          this.toggleLoader(false);

          return;
        }

        const playPromise = this.videoWrapperTarget.querySelector('video').play();

        if (typeof playPromise === 'undefined' || !playPromise) {
          this.togglePlayButton(true);
          return;
        }

        playPromise
          .catch(() => {
            this.togglePlayButton(true);
          })
          .finally(() => {
            clearTimeout(this.loaderTimeout);
            this.toggleLoader(false);
          });
      }, additionalWaitTime > 0 ? (additionalWaitTime * 1000) : 0);
    });

    // iOS requires us to call load manually...
    video.load();
  }

  handleVideoEnding() {
    const video = this.videoWrapperTarget.querySelector('video');

    video.addEventListener('timeupdate', () => {
      const { currentTime, duration } = video;
      const progress = currentTime / duration;

      if (progress >= 0.95) {
        universalTracking.event('home_video', 'complete');
      }

      if (!this.shownCookieConsent && progress >= 0.95) {
        this.showCookieConsent();
      }

      if (!this.shownEnding && progress >= 0.95) {
        this.showEnding();
      }
    });
  }

  showEnding() {
    this.shownEnding = true;

    this.endingTarget.setAttribute('aria-hidden', 'false');

    gsap.to(this.endingTarget, {
      autoAlpha: 1,
      duration: prefersReducedMotion() ? 0 : 2,
    });

    triggerWordSplit(this.application, this.endingTarget);
  }

  toggleMute() {
    const video = this.videoWrapperTarget.querySelector('video');

    if (!video) {
      return;
    }

    video.muted = !video.muted;

    universalTracking.event('home_video_toggle_mute', video.muted ? 'muted' : 'unmuted');

    this.soundButtonTarget.classList[video.muted ? 'add' : 'remove']('is-muted');
    this.soundButtonTarget.classList[video.muted ? 'remove' : 'add']('is-not-muted');
  }

  showCookieConsent() {
    this.shownCookieConsent = true;

    document.body.classList.remove('hide-cookie-consent');
  }
}

HomeVideoController.targets = [
  'loader',
  'videoWrapper',
  'playButton',
  'ending',
  'soundButton',
];

export default HomeVideoController;
